/**
 * Types for User Management (Admin) feature.
 * Used by Admin pages for Roles & Rights management.
 */

// ============================================================================
// Role Constants
// ============================================================================

/**
 * Role options for dropdowns and selectors.
 * Ordered by priority (highest first).
 */
export const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'developer', label: 'Developer' },
  { value: 'product_owner', label: 'Product Owner' },
  { value: 'visitor', label: 'Visitor' },
] as const

/**
 * Role type derived from options.
 */
export type RoleCode = typeof ROLE_OPTIONS[number]['value']

/**
 * Short labels for role badges.
 */
export const ROLE_LABELS: Record<RoleCode, string> = {
  super_admin: 'ADMIN',
  developer: 'DEV',
  product_owner: 'PO',
  visitor: 'VISITOR',
}

/**
 * Tailwind CSS classes for role badge styling.
 */
export const ROLE_STYLES: Record<RoleCode, string> = {
  super_admin: 'bg-red-100 text-red-800 border-red-200',
  developer: 'bg-blue-100 text-blue-800 border-blue-200',
  product_owner: 'bg-purple-100 text-purple-800 border-purple-200',
  visitor: 'bg-gray-100 text-gray-600 border-gray-200',
}

// ============================================================================
// User Types
// ============================================================================

/**
 * Sortable fields for user list.
 */
export type UserSortField = 'displayName' | 'email' | 'createdAt' | 'lastLoginAt'

/**
 * Sort direction.
 */
export type SortDirection = 'asc' | 'desc'

/**
 * Summary user info for list display.
 */
export interface UserSummary {
  /** Azure AD Object ID */
  id: string
  /** User's email address */
  email: string
  /** User's display name */
  displayName: string
  /** Assigned role code */
  role: RoleCode
  /** Whether user account is active */
  isActive: boolean
  /** When user was first created (ISO 8601) */
  createdAt: string
  /** Last login timestamp (ISO 8601) or null if never logged in */
  lastLoginAt: string | null
}

/**
 * Detailed user info with audit history.
 */
export interface UserDetail extends UserSummary {
  /** When user was first created (ISO 8601) */
  createdAt: string
  /** When user was last updated (ISO 8601) */
  updatedAt: string
  /** History of role changes for audit */
  roleHistory: RoleChangeRecord[]
}

/**
 * Record of a role change for audit purposes.
 */
export interface RoleChangeRecord {
  /** Previous role code (null for initial assignment) */
  previousRole: string | null
  /** New role code */
  newRole: string
  /** OID of user who made the change */
  changedBy: string
  /** Display name of user who made the change */
  changedByDisplayName: string
  /** When the change occurred (ISO 8601) */
  changedAt: string
  /** Optional reason for the change */
  reason: string | null
}

/**
 * Role definition info.
 */
export interface RoleInfo {
  /** Role code (e.g., 'developer') */
  code: string
  /** Human-readable name (e.g., 'Developer') */
  displayName: string
  /** Description of the role's purpose */
  description: string
}

// ============================================================================
// Request/Response Types
// ============================================================================

/**
 * Request parameters for listing users.
 */
export interface ListUsersRequest {
  /** Number of users to return (default: 20) */
  limit: number
  /** Number of users to skip (for pagination) */
  offset: number
  /** Optional search term (filters by name or email) */
  search?: string
  /** Optional role filter */
  role?: RoleCode
  /** Field to sort by */
  sortBy?: UserSortField
  /** Sort direction */
  sortDirection?: SortDirection
}

/**
 * Response from GET /api/admin/users
 */
export interface ListUsersResponse {
  /** Array of user summaries */
  users: UserSummary[]
  /** Total count of users matching filters */
  total: number
  /** Whether there are more users to fetch */
  hasMore: boolean
}

/**
 * Request body for PUT /api/admin/users/{oid}/role
 */
export interface UpdateUserRoleRequest {
  /** New role code to assign */
  role: RoleCode
  /** Optional reason for the change */
  reason?: string
}

/**
 * Permission value type - matches backend authorization system
 * - boolean: true/false for simple permissions
 * - 'all': full access to all resources
 * - 'own': access only to own resources
 * - 'none': no access
 */
export type PermissionValue = boolean | 'all' | 'own' | 'none'

/**
 * Budget summary embedded in CurrentUser.
 */
export interface BudgetSummary {
  /** Amount used this month in USD */
  usedUsd: number
  /** Remaining budget in USD (null for unlimited) */
  remainingUsd: number | null
  /** Percentage of budget used (0-100+) */
  percentUsed: number
}

/**
 * Current authenticated user info from GET /api/me
 */
export interface CurrentUser {
  /** Azure AD Object ID */
  id: string
  /** User's email address */
  email: string
  /** User's display name */
  displayName: string
  /** Assigned role code */
  role: RoleCode
  /** Resolved permissions map */
  permissions: Record<string, PermissionValue>
  /** Whether user account is active */
  isActive: boolean
  /** Last login timestamp (ISO 8601) or null if never logged in */
  lastLoginAt: string | null
  /** Budget summary (optional, may not be present) */
  budget?: BudgetSummary
}
